/**
 * Motobilt Intelligent Search - No API Key Required
 * Version: 2.0
 * 
 * Uses Shopify's native endpoints - no Storefront API needed
 * 
 * PRIORITY ORDER:
 * 1. SKU/Part Number (exact prefix match)
 * 2. SKU/Part Number (contains)
 * 3. Product Title (starts with)
 * 4. Product Title (contains)
 * 5. Tags/Description (contains)
 * 
 * Example: "MB1025" should show:
 * - MB1025 (exact)
 * - MB1025-L, MB1025-S (prefix)
 * - MB1025F-L, MB1025F-S (prefix)
 * - Then anything else containing MB1025
 */

(function() {
  'use strict';

  // ============================================
  // CONFIGURATION
  // ============================================
  
  const CONFIG = {
    debounceMs: 150,
    minChars: 2,
    maxDropdownProducts: 6,
    maxDropdownSuggestions: 4,
    maxQuerySuggestions: 6,
    pageResultsPerPage: 24,
    enableCache: true,
    cacheTTL: 300000, // 5 minutes
    
    // Scoring weights - SKU is king for a parts store
    weights: {
      skuExact: 1000,        // Exact SKU match
      skuStartsWith: 500,    // SKU starts with query
      skuContains: 200,      // SKU contains query
      titleStartsWith: 100,  // Title starts with query
      titleWordStarts: 80,   // A word in title starts with query
      titleContains: 50,     // Title contains query
      keywordAllMatch: 35,   // Every query word found in title, but not as one contiguous phrase
      keywordPartialMatch: 10, // Per matched query word, when not all words matched
      tagMatch: 30,          // Tag contains query
      vendorMatch: 20,       // Vendor matches
      descriptionMatch: 10,  // Description contains query

      // Similar/complementary backfill - always below every real text-match
      // weight above so genuine matches never get outranked by these.
      relatedCategory: 5,    // Same category as the closest (top) match
      relatedTag: 1,         // Each shared tag with the closest match (capped at 3)
    },

    // Boost for in-stock items
    inStockBoost: 1.1,
  };

  // ============================================
  // TYPO CORRECTIONS
  // ============================================
  
  const TYPO_CORRECTIONS = {
    // Common Motobilt product typos
    'bumpper': 'bumper', 'bumber': 'bumper', 'bumpr': 'bumper', 'bummper': 'bumper',
    'fendor': 'fender', 'fendr': 'fender', 'fendre': 'fender', 'fendder': 'fender',
    'roker': 'rocker', 'rokcer': 'rocker', 'rocer': 'rocker', 'rokkcer': 'rocker',
    'slidr': 'slider', 'slidrs': 'sliders', 'slidder': 'slider', 'sliderr': 'slider',
    'skidplate': 'skid plate', 'skidplat': 'skid plate', 'skidd': 'skid',
    'tiregate': 'tire gate', 'tiergate': 'tire gate', 'tirecarrier': 'tire carrier',
    'wench': 'winch', 'winsh': 'winch', 'whinch': 'winch', 'winchh': 'winch',
    'gril': 'grill', 'grile': 'grill', 'grilll': 'grill', 'grll': 'grill',
    'exhuast': 'exhaust', 'exaust': 'exhaust', 'exhasut': 'exhaust',
    'armour': 'armor', 'armr': 'armor', 'armorr': 'armor',
    'tailgte': 'tailgate', 'tailgait': 'tailgate', 'talegait': 'tailgate',
    'headlite': 'headlight', 'hedlight': 'headlight', 'headligt': 'headlight',
    'taillite': 'taillight', 'taillght': 'taillight', 'tailligt': 'taillight',
    'stingr': 'stinger', 'stingger': 'stinger', 'stiner': 'stinger',
    'hammar': 'hammer', 'hamer': 'hammer', 'hammr': 'hammer',
    'difuser': 'diffuser', 'diffusor': 'diffuser', 'difusor': 'diffuser',
    'suspention': 'suspension', 'suspenion': 'suspension',
    'steal': 'steel', 'stell': 'steel', 'steeel': 'steel',
    'aluminium': 'aluminum', 'alumnium': 'aluminum', 'alumnum': 'aluminum',
    'monut': 'mount', 'moutn': 'mount', 'mounts': 'mount', 'mountt': 'mount',
    'brakect': 'bracket', 'bracket': 'bracket', 'braket': 'bracket',
    'houp': 'hoop', 'hooop': 'hoop',
    'flaire': 'flare', 'flair': 'flare', 'flarr': 'flare',
    
    // Vehicle typos
    'jeep': 'jeep', 'jep': 'jeep', 'geep': 'jeep', 'jeeep': 'jeep',
    'wrangler': 'wrangler', 'wranler': 'wrangler', 'wrnagler': 'wrangler', 'rangeler': 'wrangler',
    'gladiator': 'gladiator', 'gladitor': 'gladiator', 'gladiater': 'gladiator', 'gladator': 'gladiator',
    'rubicon': 'rubicon', 'rubacon': 'rubicon', 'rubiconn': 'rubicon',
    
    // Common letter swaps
    'frount': 'front', 'fornt': 'front',
    'raer': 'rear', 'rearr': 'rear',
  };

  // ============================================
  // MERCHANT SETTINGS (from theme customizer, via mb-search-init.liquid)
  // ============================================

  const USER_SETTINGS = (typeof window !== 'undefined' && window.MotobiltSearchSettings) || {};

  function parseCustomCorrections(raw) {
    const corrections = {};
    if (!raw) return corrections;

    raw.split('\n').forEach(line => {
      const idx = line.indexOf('=');
      if (idx === -1) return;
      const typo = line.slice(0, idx).trim().toLowerCase();
      const correct = line.slice(idx + 1).trim().toLowerCase();
      if (typo && correct) corrections[typo] = correct;
    });

    return corrections;
  }

  const TYPO_CORRECTION_ENABLED = USER_SETTINGS.typoEnabled !== false;
  const ALL_TYPO_CORRECTIONS = Object.assign(
    {},
    TYPO_CORRECTIONS,
    parseCustomCorrections(USER_SETTINGS.typoCustom)
  );

  // ============================================
  // CACHE
  // ============================================
  
  class SimpleCache {
    constructor(ttl = 300000) {
      this.cache = new Map();
      this.ttl = ttl;
    }
    
    get(key) {
      const item = this.cache.get(key);
      if (!item) return null;
      if (Date.now() > item.expiry) {
        this.cache.delete(key);
        return null;
      }
      return item.value;
    }
    
    set(key, value) {
      this.cache.set(key, {
        value,
        expiry: Date.now() + this.ttl
      });
    }
    
    clear() {
      this.cache.clear();
    }
  }

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================
  
  function debounce(fn, delay) {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      return new Promise(resolve => {
        timeout = setTimeout(() => resolve(fn.apply(this, args)), delay);
      });
    };
  }
  
  // Standard edit-distance (single-row DP) between two lowercase strings.
  function levenshteinDistance(a, b) {
    if (a === b) return 0;
    const al = a.length, bl = b.length;
    if (al === 0) return bl;
    if (bl === 0) return al;

    let prevRow = new Array(bl + 1);
    for (let j = 0; j <= bl; j++) prevRow[j] = j;

    for (let i = 1; i <= al; i++) {
      const currRow = new Array(bl + 1);
      currRow[0] = i;
      for (let j = 1; j <= bl; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        currRow[j] = Math.min(
          prevRow[j] + 1,       // deletion
          currRow[j - 1] + 1,   // insertion
          prevRow[j - 1] + cost // substitution
        );
      }
      prevRow = currRow;
    }

    return prevRow[bl];
  }

  // Finds the closest known product-title word to an unrecognized query word,
  // within a small edit-distance budget scaled to word length. Returns null
  // if nothing is close enough to be confident it's a typo rather than a
  // genuinely different (if rare) word.
  function findFuzzyMatch(word, vocabulary) {
    if (!vocabulary || word.length < 3) return null;

    const maxDistance = word.length <= 4 ? 1 : 2;
    let best = null;
    let bestDistance = Infinity;
    let bestCount = 0;

    for (const [candidate, count] of vocabulary) {
      if (Math.abs(candidate.length - word.length) > maxDistance) continue;

      const distance = levenshteinDistance(word, candidate);
      if (distance > maxDistance) continue;

      if (distance < bestDistance || (distance === bestDistance && count > bestCount)) {
        best = candidate;
        bestDistance = distance;
        bestCount = count;
      }
    }

    return best;
  }

  function autoCorrect(query, vocabulary) {
    const words = query.toLowerCase().trim().split(/\s+/);
    let corrected = false;

    const result = words.map(word => {
      if (!TYPO_CORRECTION_ENABLED) return word;

      // Curated dictionary (built-in + merchant custom list) always wins.
      if (ALL_TYPO_CORRECTIONS[word]) {
        corrected = true;
        return ALL_TYPO_CORRECTIONS[word];
      }

      // Skip fuzzy matching for SKUs/part numbers/years - those must match
      // exactly, never get "corrected" into a similar-looking word.
      if (/\d/.test(word) || word.length < 3) return word;

      // Already a real word in the catalog - nothing to fix.
      if (vocabulary && vocabulary.has(word)) return word;

      const fuzzyMatch = findFuzzyMatch(word, vocabulary);
      if (fuzzyMatch) {
        corrected = true;
        return fuzzyMatch;
      }

      return word;
    });

    return {
      query: result.join(' '),
      original: query,
      wasCorrected: corrected
    };
  }
  
  function normalizeString(str) {
    return (str || '').toLowerCase().trim();
  }
  
  function extractSKU(product) {
    // Try multiple sources for SKU
    if (product.sku) return product.sku;
    if (product.variants && product.variants[0] && product.variants[0].sku) {
      return product.variants[0].sku;
    }
    // Sometimes SKU is in the title after a dash or in parentheses
    const match = (product.title || '').match(/\b(MB\d+[A-Z0-9\-]*)/i);
    if (match) return match[1];
    return '';
  }
  
  function getAllSKUs(product) {
    const skus = new Set();
    
    // Main product SKU
    if (product.sku) skus.add(product.sku.toLowerCase());
    
    // Variant SKUs
    if (product.variants) {
      product.variants.forEach(v => {
        if (v.sku) skus.add(v.sku.toLowerCase());
      });
    }
    
    // Extract from title
    const titleMatches = (product.title || '').match(/\b(MB\d+[A-Z0-9\-]*)/gi);
    if (titleMatches) {
      titleMatches.forEach(m => skus.add(m.toLowerCase()));
    }
    
    return Array.from(skus);
  }

  // ============================================
  // SCORING FUNCTION
  // ============================================
  
  function scoreProduct(product, query) {
    const q = normalizeString(query);
    const qNoDash = q.replace(/[-\s]/g, '');
    
    let score = 0;
    let matchedOn = [];
    
    // === SKU MATCHING (HIGHEST PRIORITY) ===
    const skus = getAllSKUs(product);
    
    for (const sku of skus) {
      const skuNoDash = sku.replace(/[-\s]/g, '');
      
      // Exact match
      if (sku === q || skuNoDash === qNoDash) {
        score += CONFIG.weights.skuExact;
        matchedOn.push('sku-exact');
      }
      // SKU starts with query
      else if (sku.startsWith(q) || skuNoDash.startsWith(qNoDash)) {
        score += CONFIG.weights.skuStartsWith;
        matchedOn.push('sku-prefix');
      }
      // SKU contains query
      else if (sku.includes(q) || skuNoDash.includes(qNoDash)) {
        score += CONFIG.weights.skuContains;
        matchedOn.push('sku-contains');
      }
    }
    
    // === TITLE MATCHING ===
    const title = normalizeString(product.title);
    const titleNoDash = title.replace(/[-\s]/g, '');
    const titleWords = title.split(/[\s\-_\/]+/).filter(Boolean);
    
    if (title.startsWith(q) || titleNoDash.startsWith(qNoDash)) {
      score += CONFIG.weights.titleStartsWith;
      matchedOn.push('title-prefix');
    } else {
      // Check if any word in title starts with query
      const wordStartMatch = titleWords.some(word => word.startsWith(q));
      if (wordStartMatch) {
        score += CONFIG.weights.titleWordStarts;
        matchedOn.push('title-word');
      } else if (title.includes(q) || titleNoDash.includes(qNoDash)) {
        score += CONFIG.weights.titleContains;
        matchedOn.push('title-contains');
      } else {
        // Fallback: the query doesn't appear as one contiguous phrase
        // anywhere in the title - often because punctuation splits it up
        // (e.g. title "Crusher Front Bumper w/ Stinger" vs. query
        // "bumper w stinger", where title.includes() can never match
        // because of the "/" in the middle). Instead check whether every
        // individual query word shows up *somewhere* in the title, in
        // any order - scored below a real phrase match, but still a hit
        // instead of zero results.
        const queryWords = q.split(/\s+/).filter(Boolean);
        if (queryWords.length > 1) {
          const matchedWords = queryWords.filter(qw =>
            titleWords.some(tw => tw.includes(qw))
          ).length;

          if (matchedWords === queryWords.length) {
            score += CONFIG.weights.keywordAllMatch;
            matchedOn.push('keyword-all');
          } else if (matchedWords >= 2) {
            score += matchedWords * CONFIG.weights.keywordPartialMatch;
            matchedOn.push('keyword-partial');
          }
        }
      }
    }
    
    // === TAG MATCHING ===
    const tags = Array.isArray(product.tags) 
      ? product.tags 
      : (product.tags || '').split(',').map(t => t.trim());
    
    for (const tag of tags) {
      if (normalizeString(tag).includes(q)) {
        score += CONFIG.weights.tagMatch;
        matchedOn.push('tag');
        break;
      }
    }
    
    // === VENDOR MATCHING ===
    if (product.vendor && normalizeString(product.vendor).includes(q)) {
      score += CONFIG.weights.vendorMatch;
      matchedOn.push('vendor');
    }
    
    // === DESCRIPTION MATCHING (lowest priority) ===
    const desc = normalizeString(product.body_html || product.description || '');
    if (desc.includes(q)) {
      score += CONFIG.weights.descriptionMatch;
      matchedOn.push('description');
    }
    
    // === AVAILABILITY BOOST ===
    if (product.available !== false) {
      score *= CONFIG.inStockBoost;
    }
    
    return { score, matchedOn };
  }

  // ============================================
  // MAIN SEARCH ENGINE CLASS
  // ============================================
  
  class MotobiltSearch {
    constructor() {
      this.products = [];
      this.indexed = false;
      this.cache = new SimpleCache(CONFIG.cacheTTL);
      this.recentSearches = this._loadRecentSearches();
      this.loading = false;
      this.phraseIndex = null;
      this.wordVocabulary = null;
    }
    
    // --- Product Loading ---
    
    async loadProducts() {
      if (this.indexed && this.products.length > 0) {
        return this.products;
      }
      
      if (this.loading) {
        // Wait for existing load to complete
        while (this.loading) {
          await new Promise(r => setTimeout(r, 100));
        }
        return this.products;
      }
      
      this.loading = true;
      
      try {
        // Shopify's /products.json endpoint - no API key needed
        // Limited to 250 products per page
        let allProducts = [];
        let page = 1;
        let hasMore = true;
        
        while (hasMore && page <= 10) { // Max 2500 products
          const url = `/products.json?limit=250&page=${page}`;
          const response = await fetch(url);
          
          if (!response.ok) {
            throw new Error(`Failed to fetch products: ${response.status}`);
          }
          
          const data = await response.json();
          
          if (data.products && data.products.length > 0) {
            allProducts = allProducts.concat(data.products);
            page++;
            hasMore = data.products.length === 250;
          } else {
            hasMore = false;
          }
        }
        
        this.products = allProducts;
        this.indexed = true;
        this._buildPhraseIndex();

        console.log(`[Motobilt Search] Loaded ${this.products.length} products`);
        
        return this.products;
        
      } catch (error) {
        console.error('[Motobilt Search] Failed to load products:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    }
    
    // --- Main Search ---
    
    async search(query, options = {}) {
      const trimmedQuery = (query || '').trim();
      
      if (trimmedQuery.length < CONFIG.minChars) {
        return this._emptyResult(trimmedQuery);
      }
      
      // Check cache
      const cacheKey = `search:${trimmedQuery}:${JSON.stringify(options)}`;
      if (CONFIG.enableCache) {
        const cached = this.cache.get(cacheKey);
        if (cached) return cached;
      }
      
      // Make sure products are loaded
      await this.loadProducts();
      
      // Auto-correct typos (dictionary + fuzzy match against catalog vocabulary)
      const correction = autoCorrect(trimmedQuery, this.wordVocabulary);
      const searchQuery = correction.query;
      
      // Score all products
      let results = this.products.map(product => {
        const { score, matchedOn } = scoreProduct(product, searchQuery);
        return {
          ...this._formatProduct(product),
          _score: score,
          _matchedOn: matchedOn
        };
      });
      
      // Filter to only products with a score
      results = results.filter(r => r._score > 0);
      
      // Sort by score (highest first)
      results.sort((a, b) => b._score - a._score);

      // Backfill with similar/complementary items when the true text matches
      // don't fill out a full page. The closest real match always stays
      // first; these are appended after and score below every genuine match.
      if (results.length > 0 && results.length < CONFIG.pageResultsPerPage && (options.page || 1) === 1) {
        results = results.concat(this._findRelatedProducts(results));
      }

      // Apply filters
      if (options.category) {
        results = results.filter(r => 
          normalizeString(r.productType) === normalizeString(options.category)
        );
      }
      
      if (options.minPrice !== undefined) {
        results = results.filter(r => r.price >= options.minPrice);
      }
      
      if (options.maxPrice !== undefined) {
        results = results.filter(r => r.price <= options.maxPrice);
      }
      
      if (options.inStock) {
        results = results.filter(r => r.available);
      }
      
      // Apply sorting override
      if (options.sort && options.sort !== 'relevance') {
        results = this._applySorting(results, options.sort);
      }
      
      // Pagination
      const page = options.page || 1;
      const perPage = options.perPage || CONFIG.pageResultsPerPage;
      const totalResults = results.length;
      const totalPages = Math.ceil(totalResults / perPage);
      const paginatedResults = results.slice((page - 1) * perPage, page * perPage);
      
      const response = {
        query: trimmedQuery,
        correctedQuery: correction.wasCorrected ? searchQuery : null,
        results: paginatedResults,
        totalResults,
        page,
        perPage,
        totalPages,
        hasMore: page < totalPages,
        categories: this._getCategories(results),
        priceRange: this._getPriceRange(results),
      };
      
      // Cache results
      if (CONFIG.enableCache) {
        this.cache.set(cacheKey, response);
      }
      
      return response;
    }
    
    // --- Quick Search for Dropdown ---

    async quickSearch(query) {
      const trimmedQuery = (query || '').trim();

      if (trimmedQuery.length < CONFIG.minChars) {
        return {
          suggestions: [],
          querySuggestions: [],
          products: [],
          recentSearches: this.recentSearches.slice(0, 5)
        };
      }

      // Get full results
      const results = await this.search(trimmedQuery);

      // Extract unique categories from top results for suggestions
      const categories = [];
      const seenCategories = new Set();

      for (const product of results.results.slice(0, 20)) {
        if (product.productType && !seenCategories.has(product.productType)) {
          seenCategories.add(product.productType);
          categories.push({
            type: 'category',
            text: product.productType,
            count: results.results.filter(r => r.productType === product.productType).length
          });
        }
      }

      return {
        query: trimmedQuery,
        correctedQuery: results.correctedQuery,
        suggestions: categories.slice(0, CONFIG.maxDropdownSuggestions),
        querySuggestions: this.getQuerySuggestions(trimmedQuery, CONFIG.maxQuerySuggestions),
        products: results.results.slice(0, CONFIG.maxDropdownProducts),
        totalResults: results.totalResults,
        recentSearches: this.recentSearches
          .filter(s => s.toLowerCase().includes(trimmedQuery.toLowerCase()))
          .slice(0, 3)
      };
    }

    // --- Predictive Query Suggestions ---
    // Suggests full search phrases (not products/categories) as the user
    // types, built from a word n-gram index over product titles.

    _buildPhraseIndex() {
      const index = new Map();
      const wordVocab = new Map();

      const addPhrase = (phrase) => {
        const p = phrase.trim();
        if (p.length < 2) return;
        index.set(p, (index.get(p) || 0) + 1);
      };

      for (const product of this.products) {
        const words = normalizeString(product.title).split(/[\s\-_\/]+/).filter(Boolean);

        // Real-word vocabulary for fuzzy typo matching - skip numeric/SKU
        // fragments (model years, part numbers) so they're never used as
        // "correction" targets.
        for (const w of words) {
          if (w.length >= 3 && !/\d/.test(w)) {
            wordVocab.set(w, (wordVocab.get(w) || 0) + 1);
          }
        }

        for (let n = 1; n <= 3; n++) {
          for (let i = 0; i + n <= words.length; i++) {
            addPhrase(words.slice(i, i + n).join(' '));
          }
        }
      }

      this.phraseIndex = index;
      this.wordVocabulary = wordVocab;
    }

    getQuerySuggestions(query, limit = CONFIG.maxQuerySuggestions) {
      const q = normalizeString(query);
      if (!q || !this.phraseIndex) return [];

      const matches = [];

      for (const [phrase, count] of this.phraseIndex) {
        if (phrase === q) continue; // don't just echo the exact query back
        const startsWith = phrase.startsWith(q);
        if (!startsWith && !phrase.includes(` ${q}`)) continue;
        matches.push({ phrase, count, startsWith });
      }

      matches.sort((a, b) => {
        if (a.startsWith !== b.startsWith) return a.startsWith ? -1 : 1;
        if (b.count !== a.count) return b.count - a.count;
        return a.phrase.length - b.phrase.length;
      });

      return matches.slice(0, limit).map(m => ({
        type: 'query',
        text: m.phrase,
        count: m.count
      }));
    }
    
    // --- Recent Searches ---
    
    recordSearch(query) {
      const q = (query || '').trim();
      if (!q) return;
      
      this.recentSearches = [
        q,
        ...this.recentSearches.filter(s => s.toLowerCase() !== q.toLowerCase())
      ].slice(0, 10);
      
      this._saveRecentSearches();
    }
    
    clearRecentSearches() {
      this.recentSearches = [];
      this._saveRecentSearches();
    }
    
    _loadRecentSearches() {
      try {
        return JSON.parse(localStorage.getItem('motobilt_recent_searches') || '[]');
      } catch (e) {
        return [];
      }
    }
    
    _saveRecentSearches() {
      try {
        localStorage.setItem('motobilt_recent_searches', JSON.stringify(this.recentSearches));
      } catch (e) {}
    }
    
    // --- Helpers ---
    
    _formatProduct(product) {
      const variant = product.variants && product.variants[0];
      
      return {
        id: product.id,
        title: product.title,
        handle: product.handle,
        url: `/products/${product.handle}`,
        productType: product.product_type,
        vendor: product.vendor,
        tags: Array.isArray(product.tags) ? product.tags : (product.tags || '').split(',').map(t => t.trim()),
        available: product.available !== false,
        sku: extractSKU(product),
        allSKUs: getAllSKUs(product),
        price: variant ? parseFloat(variant.price) : 0,
        compareAtPrice: variant && variant.compare_at_price ? parseFloat(variant.compare_at_price) : null,
        image: product.images && product.images[0] ? product.images[0].src : null,
        images: product.images || [],
        variantId: variant ? variant.id : null,
        variantsCount: product.variants ? product.variants.length : 0,
      };
    }
    
    // Similar/complementary items: products sharing the closest match's
    // category and/or tags, scored low enough to always rank below every
    // genuine text match (the lowest real match weight is descriptionMatch: 10).
    _findRelatedProducts(matchedResults) {
      const topResult = matchedResults[0];
      const matchedIds = new Set(matchedResults.map(r => r.id));
      const topType = normalizeString(topResult.productType);
      const topTags = new Set((topResult.tags || []).map(t => normalizeString(t)));

      return this.products
        .filter(product => !matchedIds.has(product.id))
        .map(product => {
          const formatted = this._formatProduct(product);
          let score = 0;

          if (topType && normalizeString(formatted.productType) === topType) {
            score += CONFIG.weights.relatedCategory;
          }

          const sharedTags = (formatted.tags || []).filter(t => topTags.has(normalizeString(t))).length;
          score += Math.min(sharedTags, 3) * CONFIG.weights.relatedTag;

          if (formatted.available !== false) {
            score *= CONFIG.inStockBoost;
          }

          return { ...formatted, _score: score, _matchedOn: ['related'], _related: true };
        })
        .filter(r => r._score > 0)
        .sort((a, b) => b._score - a._score)
        .slice(0, CONFIG.pageResultsPerPage - matchedResults.length);
    }

    _applySorting(results, sort) {
      const sorted = [...results];
      
      switch (sort) {
        case 'price-asc':
          sorted.sort((a, b) => a.price - b.price);
          break;
        case 'price-desc':
          sorted.sort((a, b) => b.price - a.price);
          break;
        case 'title-asc':
          sorted.sort((a, b) => a.title.localeCompare(b.title));
          break;
        case 'title-desc':
          sorted.sort((a, b) => b.title.localeCompare(a.title));
          break;
        case 'newest':
          // Would need created_at, fall back to ID
          sorted.sort((a, b) => b.id - a.id);
          break;
      }
      
      return sorted;
    }
    
    _getCategories(results) {
      const counts = {};
      for (const r of results) {
        if (r.productType) {
          counts[r.productType] = (counts[r.productType] || 0) + 1;
        }
      }
      return Object.entries(counts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);
    }
    
    _getPriceRange(results) {
      const prices = results.map(r => r.price).filter(p => p > 0);
      if (!prices.length) return { min: 0, max: 0 };
      return {
        min: Math.min(...prices),
        max: Math.max(...prices)
      };
    }
    
    _emptyResult(query) {
      return {
        query,
        correctedQuery: null,
        results: [],
        totalResults: 0,
        page: 1,
        perPage: CONFIG.pageResultsPerPage,
        totalPages: 0,
        hasMore: false,
        categories: [],
        priceRange: { min: 0, max: 0 }
      };
    }
  }

  // ============================================
  // GLOBAL INSTANCE
  // ============================================
  
  window.MotobiltSearch = new MotobiltSearch();

})();
